import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { KeyRound, ArrowRight, AlertCircle, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Three-step security-question password reset.
 *   Step 1: enter email → fetch the user's 3 questions
 *   Step 2: answer all 3 → server returns a one-time 15-min reset_token
 *   Step 3: set new password → server consumes the token, redirect to /login
 *
 * No email is ever sent. Whole flow is rate-limited by the lockout machinery.
 */
export const ForgotPassword = () => {
  usePageMeta({
    title: 'Reset Password — Epicurea Nexus',
    description: 'Reset your Epicurea Nexus password via security questions.',
  });
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState(['', '', '']);
  const [resetToken, setResetToken] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => { setError(''); setInfo(''); };

  const submitEmail = async (e) => {
    e.preventDefault();
    reset(); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password/start`, {
        email: email.trim().toLowerCase(),
      });
      if (!data.success || !data.questions?.length) {
        setError(data.message || 'No password reset is available for this email.');
        return;
      }
      setQuestions(data.questions);
      setAnswers(data.questions.map(() => ''));
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not start reset.');
    } finally { setBusy(false); }
  };

  const submitAnswers = async (e) => {
    e.preventDefault();
    reset(); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password/verify`, {
        email: email.trim().toLowerCase(),
        answers,
      });
      setResetToken(data.reset_token);
      setInfo(`Verified. Set your new password — link valid for ${data.expires_in_minutes} minutes.`);
      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Verification failed.');
    } finally { setBusy(false); }
  };

  const submitNewPwd = async (e) => {
    e.preventDefault();
    reset();
    if (newPwd.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPwd !== newPwd2) { setError('Passwords do not match'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password/complete`, {
        reset_token: resetToken,
        new_password: newPwd,
      });
      setInfo('Password updated. Redirecting to login…');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not update password.');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-background"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <ShieldCheck className="h-10 w-10 text-primary mb-2" />
          <h1 className="text-2xl font-bold text-primary">Reset Password</h1>
          <p className="text-xs text-text-muted tracking-[0.2em] uppercase mt-1">
            via Security Questions
          </p>
        </div>

        <div className="bg-white border border-border rounded-md shadow-lg p-8 space-y-5">
          <Stepper step={step} />

          {step === 1 && (
            <form onSubmit={submitEmail} className="space-y-4">
              <Field
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                autoFocus
                testid="fp-email"
                placeholder="your@email.com"
              />
              <SubmitBtn busy={busy} testid="fp-step1-submit">Continue</SubmitBtn>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitAnswers} className="space-y-4">
              <p className="text-xs text-text-secondary">
                Answer all 3 security questions for <strong>{email}</strong> exactly.
                Answers are case-insensitive; spelling matters.
              </p>
              {questions.map((q, i) => (
                <Field
                  key={i}
                  label={`Q${i + 1}: ${q}`}
                  value={answers[i]}
                  onChange={(v) => {
                    const next = [...answers]; next[i] = v; setAnswers(next);
                  }}
                  testid={`fp-answer-${i}`}
                />
              ))}
              <SubmitBtn busy={busy} testid="fp-step2-submit">Verify Answers</SubmitBtn>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={submitNewPwd} className="space-y-4">
              <Field
                label="New Password (min 8 chars)"
                type="password"
                value={newPwd}
                onChange={setNewPwd}
                autoFocus
                testid="fp-new-password"
              />
              <Field
                label="Confirm New Password"
                type="password"
                value={newPwd2}
                onChange={setNewPwd2}
                testid="fp-confirm-password"
              />
              <SubmitBtn busy={busy} testid="fp-step3-submit">Update Password</SubmitBtn>
            </form>
          )}

          {error && (
            <div className="bg-red-50 border-l-4 border-state-error rounded p-3 flex items-start gap-2" data-testid="fp-error">
              <AlertCircle className="h-4 w-4 text-state-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-state-error">{error}</p>
            </div>
          )}
          {info && (
            <div className="bg-emerald-50 border-l-4 border-state-success rounded p-3 flex items-start gap-2" data-testid="fp-info">
              <CheckCircle2 className="h-4 w-4 text-state-success flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-900">{info}</p>
            </div>
          )}

          <div className="text-center pt-2 border-t border-border">
            <Link to="/login" className="text-xs text-primary font-semibold hover:underline">
              ← Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, type = 'text', value, onChange, autoFocus, placeholder, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoFocus={autoFocus}
      placeholder={placeholder}
      className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
      required
      data-testid={testid}
    />
  </div>
);

const SubmitBtn = ({ busy, children, testid }) => (
  <Button
    type="submit"
    disabled={busy}
    className="w-full bg-primary hover:bg-primary-hover text-white py-3"
    data-testid={testid}
  >
    {busy ? 'Working…' : children}
    {!busy && <ArrowRight className="h-4 w-4 ml-2 inline" />}
  </Button>
);

const Stepper = ({ step }) => (
  <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-text-muted font-semibold" data-testid="fp-stepper">
    {[
      { n: 1, label: 'Email' },
      { n: 2, label: 'Answers' },
      { n: 3, label: 'New Password' },
    ].map((s, i) => (
      <React.Fragment key={s.n}>
        <div className={`flex items-center gap-2 ${step === s.n ? 'text-primary' : step > s.n ? 'text-state-success' : ''}`}>
          <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
            step === s.n ? 'bg-primary text-white'
              : step > s.n ? 'bg-emerald-100 text-state-success'
              : 'bg-slate-100 text-text-muted'
          }`}>
            {step > s.n ? <CheckCircle2 className="h-3 w-3" /> : s.n}
          </span>
          {s.label}
        </div>
        {i < 2 && <span className="flex-1 mx-2 border-t border-border" />}
      </React.Fragment>
    ))}
  </div>
);

export default ForgotPassword;
